/**
 * @file RedeliveryBadge.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description The Redelivery pill on an order (or its PO) that was refused on
 *              delivery: "Held at <store>", "Redelivery 21/10/26" or
 *              "Redelivered 21/10/26". Hover: refused on, why, where it is
 *              held, when it goes again; a click opens the Redelivery window
 *              (RedeliverOrderModal — read-only without the permission).
 *              Nothing renders for an order never refused.
 */

import React, { useState } from 'react';
import RedeliverOrderModal from './RedeliverOrderModal';
import { redeliveryPillText, redeliveryTitle, effectiveRedeliveryStatus, REDELIVERY_STATUS_DELIVERED } from '../utils/redelivery';
import './Redelivery.css';

/**
 * @param {object}   redelivery  The order's latest redelivery (rows' `redelivery`).
 * @param {number}   orderId
 * @param {function} onChanged   After the window saved something.
 * @param {string}   variant     'badge' (default) or 'icon' for tight table cells.
 */
function RedeliveryBadge({ redelivery, orderId, onChanged, variant = 'badge' }) {
  const [open, setOpen] = useState(false);
  if (!redelivery || !orderId) return null;
  const words = redeliveryPillText(redelivery);
  if (!words) return null;
  const delivered = effectiveRedeliveryStatus(redelivery) === REDELIVERY_STATUS_DELIVERED;

  const show = (e) => { e.stopPropagation(); setOpen(true); };
  return (
    <>
      <span
        className={`redelivery-badge${delivered ? ' is-delivered' : ''}${variant === 'icon' ? ' is-icon' : ''}`}
        title={`${redeliveryTitle(redelivery)}\nClick for the redelivery.`}
        role="button"
        tabIndex={0}
        data-read-safe
        onClick={show}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') show(e); }}
      >
        <span className="material-symbols-rounded">{delivered ? 'local_shipping' : 'warehouse'}</span>
        {variant !== 'icon' && words}
      </span>
      {open && (
        <span onClick={(e) => e.stopPropagation()}>
          <RedeliverOrderModal
            orderId={orderId}
            onClose={() => setOpen(false)}
            onDone={() => onChanged && onChanged()}
          />
        </span>
      )}
    </>
  );
}

export default RedeliveryBadge;
